// Mui
import constant from "@/config/constant";
import { CurrencyFormat } from "@/config/normalConfig";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import { createStyles, createTheme, styled } from "@mui/material/styles";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import { makeStyles } from "@mui/styles";
import { GridEditDateCell } from "@mui/x-data-grid";
import clsx from "clsx";

import formatStocktakeStatus from "@/lib/formatStocktakeStatus";
import {
  ApproveButton,
  EmployeeShoppingImagePreview,
  VerifyButton,
} from "@/page/employeeShoppingPage";
import { DownLoadTemplate, UploadExcel } from "@/page/systemSettingProductPage";
import Typography from "@mui/material/Typography";
import _ from "lodash";
import moment from "moment-timezone";
import React from "react";
import validator from "validator";

const defaultTheme = createTheme();
const useStyles = makeStyles(
  (theme) =>
    createStyles({
      root: {
        border: `1px solid ${theme.palette.divider}`,
        position: "relative",
        overflow: "hidden",
        width: "100%",
        height: 26,
        borderRadius: 2,
      },
      value: {
        position: "absolute",
        lineHeight: "24px",
        width: "100%",
        display: "flex",
        justifyContent: "center",
      },
      bar: {
        height: "100%",
        "&.low": {
          backgroundColor: "#f44336",
        },
        "&.medium": {
          backgroundColor: "#efbb5aa3",
        },
        "&.high": {
          backgroundColor: "#088208a3",
        },
      },
    }),
  { defaultTheme }
);

const ProgressBar = React.memo(function ProgressBar(props) {
  const { value } = props;
  const valueInPercent = value;
  const classes = useStyles();

  return (
    <div className={classes.root}>
      <div
        className={classes.value}
      >{`${valueInPercent.toLocaleString()} %`}</div>
      <div
        className={clsx(classes.bar, {
          low: valueInPercent < 30,
          medium: valueInPercent >= 30 && valueInPercent <= 70,
          high: valueInPercent > 70,
        })}
        style={{ maxWidth: `${valueInPercent}%` }}
      />
    </div>
  );
});
export function renderProgress(params) {
  return <ProgressBar value={Number(params.value)} />;
}
export const NoticeTableColumn = [
  {
    field: "title",
    headerName: "標題",
    flex: 1,
  },
  {
    field: "notice_created_by_fullname",
    headerName: "建立人",
    width: 150,
  },
  {
    field: "time_created",
    headerName: "建立日期",
    width: 150,
  },
  {
    field: "notice_modified_by_fullname",
    headerName: "最後修改人",
    width: 150,
  },
  {
    field: "time_modified",
    headerName: "最後修改日期",
    width: 150,
  },
  {
    field: "status",
    headerName: "狀態",
    sortable: false,
    renderCell: (params) => {
      return (
        <Chip
          label={params.row.status ? "發佈" : "草稿"}
          color={params.row.status ? "success" : "error"}
        />
      );
    },
  },
];
export const LandingTableColumn = [
  {
    field: "brand",
    headerName: "品牌",
    flex: 1,
  },
  {
    field: "product",
    headerName: "產品",
    flex: 1,
  },
  {
    field: "source",
    headerName: "來源",
    flex: 1,
  },
  {
    field: "time_created",
    headerName: "建立日期",
    width: 150,
  },
  {
    field: "status",
    headerName: "狀態",
    sortable: false,
    renderCell: (params) => {
      return (
        <Chip
          label={params.row.status ? "發佈" : "草稿"}
          color={params.row.status ? "success" : "error"}
        />
      );
    },
  },
];

export const LandingFormTableColumn = [
  {
    field: "id",
    headerName: "id",
    flex: 1,
  },
  {
    field: "name",
    headerName: "來源",
    flex: 1,
  },
  {
    field: "time_created",
    headerName: "建立日期",
    width: 150,
  },
];

export const StaffTableColumn = [
  {
    field: "staff_code",
    headerName: "員工編號",
    minWidth: 120,
    flex: 1,
  },
  {
    field: "full_name",
    headerName: "員工全名",
    minWidth: 120,
    flex: 1,
  },
  {
    field: "alias",
    headerName: "員工別名",
    minWidth: 120,
    flex: 1,
  },

  {
    field: "role_types_name",
    headerName: "類別",
    minWidth: 120,
    flex: 1,
  },
  {
    field: "work_name",
    headerName: "上班地方",
    minWidth: 120,
    flex: 1,
  },
  {
    field: "is_delete",
    headerName: "狀態",
    sortable: false,
    renderCell: (params) => {
      return (
        <Chip
          label={!params.row.is_delete ? "在職" : "離職"}
          color={!params.row.is_delete ? "success" : "error"}
        />
      );
    },
  },
];

export const DailyTableColumn = [
  {
    field: "work_shortcode",
    headerName: "店鋪編號",
    maxWidth: 80,
    flex: 1,
  },
  {
    field: "combine_name",
    headerName: "店鋪全名",
    minWidth: 120,
    flex: 1,
  },
  {
    field: "work_total_amount",
    headerName: "店鋪銷售",
    minWidth: 70,
    flex: 1,

    type: "number",
    renderCell: (params) => {
      return CurrencyFormat(params.value);
    },
  },
  {
    field: "target_total_amount",
    headerName: "店銷目標",
    type: "number",
    minWidth: 70,
    flex: 1,
    renderCell: (params) => {
      return CurrencyFormat(params.value);
    },
  },
  {
    field: "sales_vs_target",
    headerName: "到數%",
    width: 70,
    flex: 1,
    align: "right",
    headerAlign: "right",
    cellClassName: (params) => {
      if (params.value >= 0) {
        return "positive--cell";
      } else {
        return "negative--cell";
      }
    },
    valueFormatter: (value) =>
      `${new Intl.NumberFormat("en", { minimumFractionDigits: 1 }).format(
        _.round(value, 1)
      )}%`,
  },
];
export const ReturnInvoiceTableColumn = [
  {
    field: "invoice_id",
    headerName: "發票編號",
    flex: 1,
  },
  {
    field: "alias",
    headerName: "員工名稱",
    flex: 1,
  },
  {
    field: "work_shortcode",
    headerName: "退貨地點",
    flex: 1,
  },
  {
    field: "reason",
    headerName: "退貨原因",
    flex: 1,
  },
  {
    field: "qty",
    headerName: "退貨數量",
    flex: 1,
    renderCell: (params) => {
      const sum = params.row.item.reduce((prev, cur) => {
        let temp = prev;
        if (cur.exchange_qty > 0) {
          temp = prev + cur.exchange_qty;
        }
        return temp;
      }, 0);
      return sum;
    },
  },
  {
    field: "time_created",
    headerName: "退貨時間",
    flex: 1,

    renderCell: (params) => {
      return moment(params.row.time_created).format("YYYY-MM-DD HH:mm:ss");
    },
  },
];
export const ExchangeInvoiceTableColumn = [
  {
    field: "invoice_id",
    headerName: "發票編號",
    flex: 1,
  },
  {
    field: "alias",
    headerName: "員工名稱",
    flex: 1,
  },
  {
    field: "work_shortcode",
    headerName: "換貨地點",
    flex: 1,
  },
  {
    field: "reason",
    headerName: "換貨原因",
    flex: 1,
  },
  {
    field: "qty",
    headerName: "換貨數量	",
    flex: 1,
    renderCell: (params) => {
      const sum = params.row.item.reduce((prev, cur) => {
        let temp = prev;
        if (cur.exchange_qty > 0) {
          temp = prev + cur.exchange_qty;
        }
        return temp;
      }, 0);
      return sum;
    },
  },
  {
    field: "time_created",
    headerName: "換貨時間",
    flex: 1,

    renderCell: (params) => {
      return moment(params.row.time_created).format("YYYY-MM-DD HH:mm:ss");
    },
  },
];
export const TransferInvoiceTableColumn = [
  {
    field: "invoice_id",
    headerName: "發票編號",
    flex: 1,
  },
  {
    field: "alias",
    headerName: "員工名稱",
    flex: 1,
  },
  {
    field: "from_staff_code",
    headerName: "調出",
    flex: 1,

    renderCell: (params) => {
      return `${params.row.from_staff_code} - ${params.row.from_alias}`;
    },
  },
  {
    field: "icon",
    headerName: "",
    flex: 1,
    sortable: false,
    renderCell: (params) => {
      return <ArrowForwardIcon></ArrowForwardIcon>;
    },
  },
  {
    field: "to_staff_code",
    headerName: "調進",
    flex: 1,

    renderCell: (params) => {
      return `${params.row.to_staff_code} - ${params.row.to_alias}`;
    },
  },
  {
    field: "reason",
    headerName: "調貨原因	",
    flex: 1,
  },
  {
    field: "remark",
    headerName: "備註",
    flex: 1,
  },
  {
    field: "qty",
    headerName: "調貨數量",
    flex: 1,
    renderCell: (params) => {
      const sum = params.row.item.reduce((prev, cur) => {
        let temp = prev;
        if (cur.transfer_qty > 0) {
          temp = prev + cur.transfer_qty;
        }
        return temp;
      }, 0);
      return sum;
    },
  },
  {
    field: "time_created",
    headerName: "調貨時間",
    flex: 1,

    renderCell: (params) => {
      return moment(params.row.time_created).format("YYYY-MM-DD HH:mm:ss");
    },
  },
];
export const CustomerTableColumn = [
  {
    field: "fullname",
    headerName: "顧客全名",
    minWidth: 120,
    flex: 1,
  },
  {
    field: "phone",
    headerName: "電話號碼",
    minWidth: 120,
    renderCell: (params) => {
      return params.row.phone === "00000000" ? "N/A" : params.row.phone;
    },
  },
  {
    field: "staff_code",
    headerName: "員工編號",
    minWidth: 120,
  },
  {
    field: "staff_name",
    headerName: "員工名稱",
    minWidth: 120,
  },
  {
    field: "Sum",
    headerName: "領取物品總數",
    minWidth: 120,
  },
  {
    field: "time_created",
    headerName: "建立日期",
    minWidth: 120,
    renderCell: (params) => {
      return moment(params.row.time_created).format("YYYY-MM-DD") ===
        "1970-01-01"
        ? "N/A"
        : moment(params.row.time_created).format("YYYY-MM-DD");
    },
  },
];

export const InvoiceTableColumn = [
  {
    field: "tx_date",
    headerName: "單據日子",
    flex: 1,
  },
  {
    field: "total",
    headerName: "單據總數",
    flex: 1,
    renderCell: (params) => {
      return CurrencyFormat(params.value);
    },
  },
];

export const TargetSettingColumn = [
  {
    field: "target_total_amount",
    headerName: "目標總額",
    flex: 150,
  },
  {
    field: "target_month",
    headerName: "目標月份",
    width: 150,
  },
  {
    field: "target_year",
    headerName: "目標年份",
    width: 150,
  },
  {
    field: "time_created",
    headerName: "建立日期",
    width: 150,
  },
];

export const CustomerDailyTableColumn = [
  {
    field: "staff_name",
    headerName: "員工名稱",
    minWidth: 120,
    width: 240,
  },
  {
    field: "cust_name",
    headerName: "客人名稱",
    minWidth: 120,
    width: 240,
  },
  {
    field: "item_name",
    headerName: "領取貨品",
    flex: 1,
  },
  {
    field: "giveaway_invoiceitem_qty",
    headerName: "領取數量",
    minWidth: 50,
    maxWidth: 100,
    flex: 1,
  },
  {
    field: "work_name",
    headerName: "換領地點",
    minWidth: 50,
    maxWidth: 100,
    flex: 1,
  },

  {
    field: "time_created",
    headerName: "換領時間",
    minWidth: 50,
    maxWidth: 100,
    flex: 1,
  },

  {
    field: "is_checked",
    headerName: "狀態",
    editable: true,
    sortable: false,
    renderCell: (params) => {
      return (
        <Chip
          label={params.row.is_checked ? "已檢查" : "未檢查"}
          color={params.row.is_checked ? "success" : "info"}
        />
      );
    },
  },
];

export const RefundTableColumn = [
  {
    field: "full_name",
    headerName: "經手人",
    minWidth: 120,
    flex: 1,
  },
  {
    field: "item_name",
    headerName: "退貨名稱",
    minWidth: 120,
    flex: 1,
  },
  {
    field: "refund_qty",
    headerName: "退貨數量",
    minWidth: 120,
    flex: 1,
  },
  {
    field: "refund_date",
    headerName: "退貨日子",
    minWidth: 120,
    flex: 1,
    renderCell: (params) => {
      return moment(params.formattedValue).format("YYYY-MM-DD");
    },
  },
  // {
  //   field: "refund_total",
  //   headerName: "退貨總數",
  //   minWidth: 120,
  //   flex: 1,
  // },
  {
    field: "invoice_created",
    headerName: "建立日期",
    minWidth: 120,
    flex: 1,
    renderCell: (params) => {
      return moment(params.value).format("YYYY-MM-DD");
    },
  },
];
const StyledTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
  },
}));

function DateEditInputCell(props) {
  const { error } = props;

  return (
    <StyledTooltip open={!!error} title={error}>
      <Box>
        <GridEditDateCell {...props} error={!!error} />
      </Box>
    </StyledTooltip>
  );
}
function renderDateEditCell(params) {
  return <DateEditInputCell {...params} />;
}
export const CustomerDetailColumn = [
  {
    field: "item_name",
    headerName: "物品名稱",
    minWidth: 120,
    flex: 1,
  },
  {
    field: "giveaway_invoiceitem_qty",
    headerName: "領取數量",
    minWidth: 120,
    flex: 1,
  },

  {
    field: "time_created",
    headerName: "領取日期",
    minWidth: 120,
    flex: 1,
    renderCell: (params) => {
      return moment(params.value).format("YYYY-MM-DD");
    },
  },
];

export const ProductSystemTableColumn = [
  {
    field: "m18_product_code",
    headerName: "M18 號碼",
    minWidth: 120,
    flex: 1,
    editable: false,
  },
  {
    field: "mannings_product_code",
    headerName: "萬寧號碼",
    minWidth: 120,
    flex: 1,
    editable: false,
  },
  {
    field: "product_name",
    headerName: "產品名稱",
    width: 300,
    editable: false,
  },
  {
    field: "product_unit_price",
    headerName: "產品單價價錢",
    minWidth: 120,
    flex: 1,
    editable: false,
  },
  {
    field: "discount_name",
    headerName: "折扣名稱",
    minWidth: 120,
    flex: 1,
    editable: false,
    renderCell: (params) => {
      return params.value ?? "沒有折扣";
    },
  },
  {
    field: "product_discount_price",
    headerName: "產品單價折扣",
    minWidth: 120,
    flex: 1,
    editable: false,
  },

  {
    field: "product_discount_startdate",
    headerName: "開始時間",
    minWidth: 120,
    flex: 1,
    editable: false,
    renderCell: (params) => {
      return moment(params.value).format("YYYY-MM-DD");
    },
  },
  {
    field: "product_discount_enddate",
    headerName: "結束時間",
    minWidth: 120,
    flex: 1,
    editable: false,
    renderCell: (params) => {
      return moment(params.value).format("YYYY-MM-DD");
    },
  },
  {
    field: "upload",
    headerName: "上傳分貨",
    width: 80,
    align: "center",
    editable: false,
    renderCell: UploadExcel,
  },
  {
    field: "template",
    headerName: "下載分貨",
    width: 80,
    align: "center",
    editable: false,
    renderCell: DownLoadTemplate,
  },
  {
    field: "is_active",
    headerName: "狀態",
    width: 80,
    sortable: false,
    renderCell: (params) => {
      return (
        <Chip
          label={params.row.is_active ? "開啓" : "關閉"}
          color={params.row.is_active ? "success" : "error"}
        />
      );
    },
  },
];
export function ProductManageTableColumn(types) {
  return [
    {
      field: "brand_types_name",
      headerName: "品牌",
      type: "singleSelect",
      valueOptions: ({ row }) => {
        const options = [];
        types?.map((type) => options.push(type.brand_types_name));
        return options;
      },
      minWidth: 120,
      flex: 1,
      editable: false,
    },
    {
      field: "m18_product_code",
      headerName: "M18 號碼",
      minWidth: 120,
      flex: 1,
      preProcessEditCellProps: (params) => {
        const hasError = () => {
          let errorMessage = "";
          if (validator.isEmpty(params.props.value.trim())) {
            errorMessage = "Empty value is not allowed";
          } else {
            errorMessage = null;
          }
          return errorMessage;
        };
        return { ...params.props, error: hasError() };
      },
      editable: true,
    },
    {
      field: "mannings_product_code",
      headerName: "萬寧號碼",
      minWidth: 120,
      flex: 1,
      preProcessEditCellProps: (params) => {
        const hasError = () => {
          let errorMessage = "";
          if (validator.isEmpty(params.props.value.trim())) {
            errorMessage = "Empty value is not allowed";
          } else {
            errorMessage = null;
          }
          return errorMessage;
        };
        return { ...params.props, error: hasError() };
      },
      editable: true,
    },
    {
      field: "product_name",
      headerName: "產品名稱",
      width: 300,
      flex: 2,
      preProcessEditCellProps: (params) => {
        const hasError = () => {
          let errorMessage = "";
          if (validator.isEmpty(params.props.value.trim())) {
            errorMessage = "Empty value is not allowed";
          } else {
            errorMessage = null;
          }
          return errorMessage;
        };
        return { ...params.props, error: hasError() };
      },
      editable: true,
    },
    {
      field: "product_unit_price",
      headerName: "產品單價價錢",
      minWidth: 120,
      flex: 1,
      preProcessEditCellProps: (params) => {
        const hasError = () => {
          let errorMessage = "";
          if (validator.isEmpty(params.props.value.trim())) {
            errorMessage = "Empty value is not allowed";
          } else if (!validator.isNumeric(params.props.value)) {
            errorMessage = "Value may contain string/text/character";
          } else {
            errorMessage = null;
          }
          return errorMessage;
        };
        return { ...params.props, error: hasError() };
      },
      editable: true,
    },
    {
      field: "region_name",
      headerName: "區域",
      minWidth: 120,
      flex: 1,
    },
    {
      field: "type",
      headerName: "種類",
      minWidth: 120,
      flex: 1,
      valueGetter: (value) => {
        const display = _.map(value, (v) => constant.productType[v]);
        return display.join(",");
      },
    },
    {
      field: "muti_region",
      headerName: "共用code",
      editable: false,
      renderCell: (params) => {
        return (
          <Chip
            label={params.row.muti_region ? "開啓" : "關閉"}
            color={params.row.muti_region ? "success" : "error"}
          />
        );
      },
    },
    {
      field: "is_active",
      headerName: "報數列表",
      editable: false,
      renderCell: (params) => {
        return (
          <Chip
            label={params.row.is_active ? "開啓" : "關閉"}
            color={params.row.is_active ? "success" : "error"}
          />
        );
      },
    },
  ];
}
export const workLocationManageTableColumn = [
  {
    field: "work_shortcode",
    headerName: "店舖編號",
    minWidth: 120,
    flex: 1,
    editable: false,
  },
  {
    field: "work_name",
    headerName: "店舖名稱",
    minWidth: 120,
    flex: 1,
    editable: false,
  },
  {
    field: "region_name",
    headerName: "Region",
    minWidth: 120,
    flex: 1,
    editable: false,
  },
  {
    field: "district_name",
    headerName: "District",
    minWidth: 120,
    flex: 1,
    editable: false,
  },
  {
    field: "area_name",
    headerName: "Area",
    minWidth: 120,
    flex: 1,
    editable: false,
  },
  {
    field: "work_address",
    headerName: "店舖地址",
    width: 300,
    flex: 2,
    editable: false,
  },
  {
    field: "work_telephone",
    headerName: "店舖電話",
    minWidth: 120,
    flex: 1,
    editable: false,
  },
];

export const mergeDeliveryOrderManageTableColumn = [
  {
    field: "staff_code",
    headerName: "員工編號",
    minWidth: 120,
    flex: 1,
    editable: false,
  },
  {
    field: "full_name",
    headerName: "員工名稱",
    minWidth: 120,
    flex: 1,
    editable: false,
  },
  {
    field: "item_name",
    headerName: "贈品名稱",
    minWidth: 120,
    flex: 1,
    editable: false,
  },
  {
    field: "delivery_qty",
    headerName: "件數",
    minWidth: 120,
    flex: 1,
    editable: false,
  },
];
export const stocktakeTableColumn = [
  {
    field: "staff_code",
    headerName: "員工編號",
    minWidth: 120,
    flex: 1,
    editable: false,
  },
  {
    field: "full_name",
    headerName: "員工名稱",
    minWidth: 120,
    flex: 1,
    editable: false,
  },
  {
    field: "time_created",
    headerName: "盤點時間",
    minWidth: 120,
    flex: 1,
    editable: false,
    renderCell: (params) => {
      return moment(params.row.time_created).format("YYYY-MM-DD HH:mm:ss");
    },
  },
  {
    field: "status",
    headerName: "狀態",
    width: 300,
    flex: 1,
    editable: false,
    renderCell: (params) => {
      return formatStocktakeStatus(params.row);
    },
  },
];
export const deliveryOrderManageTableColumn = [
  {
    field: "si_number",
    headerName: "SI",
    minWidth: 150,
    flex: 1,
    editable: false,
  },
  {
    field: "stockonhand_refill_id",
    headerName: "申請編號",
    minWidth: 120,
    flex: 1,
    editable: false,
  },
  {
    field: "staff_code",
    headerName: "員工編號",
    minWidth: 120,
    flex: 1,
    editable: false,
  },
  {
    field: "alias",
    headerName: "員工名稱",
    minWidth: 120,
    flex: 1,
    editable: false,
  },
  {
    field: "sum_of_delivery_qty",
    headerName: "運送總件數",
    minWidth: 40,
    flex: 1,
    editable: false,
  },
  {
    field: "sum_of_actual_qty",
    headerName: "實際收到件數",
    minWidth: 40,
    flex: 1,
    editable: false,
    renderCell: (params) => {
      return params.row.status === "InTransit" || params.row.status === "Void"
        ? "--"
        : params.row.sum_of_actual_qty;
    },
  },
  {
    field: "diffenert",
    headerName: "件數差額",
    minWidth: 40,
    flex: 1,
    editable: false,
    type: "number",
  },
  {
    field: "time_created",
    headerName: "創建時間",
    minWidth: 150,
    flex: 1,
    editable: false,
    renderCell: (params) => {
      return moment(params.row.time_created).format("YYYY-MM-DD HH:mm:ss");
    },
  },
  {
    field: "time_arrived",
    headerName: "收貨時間",
    minWidth: 150,
    flex: 1,
    editable: false,
    renderCell: (params) => {
      return params.row.status === "InTransit" || params.row.status === "Void"
        ? "--"
        : moment(params.row.time_arrived).format("YYYY-MM-DD HH:mm:ss");
    },
  },
  {
    field: "status",
    headerName: "狀態",
    width: 120,
    flex: 1,
    editable: false,
  },
];

export const promotionTableColum = [
  {
    field: "name",
    headerName: "名稱",
    minWidth: 120,
    flex: 1,
    editable: false,
  },
  {
    field: "date",
    headerName: "日期",
    valueFormatter: (value) => moment(value).format("YYYY-MM-DD"),

    minWidth: 120,
    flex: 1,
    editable: false,
  },
  {
    field: "type",
    headerName: "類型",
    valueFormatter: (value) => (value === "qty" ? "數量" : "銷售數"),
    minWidth: 120,
    flex: 1,
    editable: false,
  },
];
export const giveAwayManageTableColumn = [
  {
    field: "m18_product_code",
    headerName: "M18號碼",
    minWidth: 120,
    flex: 1,
    editable: false,
  },
  {
    field: "item_brand",
    headerName: "品牌",
    minWidth: 120,
    flex: 1,
    editable: false,
  },
  {
    field: "item_name",
    headerName: "產品名稱",
    minWidth: 120,
    flex: 2,
    editable: false,
  },
  {
    field: "item_unit_price",
    headerName: "產品單價價錢",
    width: 300,
    flex: 1,
    editable: false,
  },
  {
    field: "region_ids",
    headerName: "區域",
    width: 300,
    flex: 1,
    type: "singleSelect",
    valueGetter: (value) => {
      const display = _.map(value, (v) => constant.regionType[v]);
      return display.join(",");
    },
    valueOptions: ({ row }) => {
      const options = ["香港", "澳門"];
      return options;
    },
    editable: false,
  },
  {
    field: "is_active",
    headerName: "",
    editable: false,
    renderCell: (params) => {
      return (
        <Chip
          label={params.row.is_active ? "開啓" : "關閉"}
          color={params.row.is_active ? "success" : "error"}
        />
      );
    },
  },
];

export const giveAwayManageActiveTableColumn = [
  {
    field: "m18_product_code",
    headerName: "M18號碼",
    minWidth: 120,
    flex: 1,
    editable: false,
  },
  {
    field: "item_brand",
    headerName: "品牌",
    minWidth: 120,
    flex: 1,
    editable: false,
  },
  {
    field: "item_name",
    headerName: "產品名稱",
    minWidth: 120,
    flex: 2,
    editable: false,
  },
  {
    field: "item_unit_price",
    headerName: "產品單價價錢",
    width: 300,
    flex: 1,
    editable: false,
  },
  {
    field: "list_datetime",
    headerName: "月份",
    width: 300,
    flex: 1,
    editable: false,
  },
  {
    field: "is_active",
    headerName: "狀態",
    editable: false,
    renderCell: (params) => {
      return (
        <Chip
          label={params.row.is_active ? "開啓" : "關閉"}
          color={params.row.is_active ? "success" : "error"}
        />
      );
    },
  },
];
export const RosterStaffTableColumn = [
  {
    field: "alias",
    headerName: "員工別名",
    minWidth: 120,
    flex: 1,
  },
  {
    field: "staff_code",
    headerName: "員工編號",
    minWidth: 120,
    flex: 1,
  },
  {
    field: "full_name",
    headerName: "員工全名",
    minWidth: 120,
    flex: 1,
  },
  {
    field: "work_name",
    headerName: "上班地方",
    minWidth: 120,
    flex: 1,
  },
];

export const RosterShopTableColumn = [
  {
    field: "work_shortcode",
    headerName: "店舖編號",
    minWidth: 120,
    flex: 1,
    editable: false,
  },
  {
    field: "work_name",
    headerName: "店舖名稱",
    minWidth: 120,
    flex: 1,
    editable: false,
  },
  {
    field: "work_district",
    headerName: "店舖地區",
    minWidth: 120,
    flex: 1,
    editable: false,
  },
  {
    field: "work_address",
    headerName: "店舖地址",
    width: 300,
    flex: 2,
    editable: false,
  },
  {
    field: "work_telephone",
    headerName: "店舖電話",
    minWidth: 120,
    flex: 1,
    editable: false,
  },
];
export const StockOnHandTableColumn = [
  {
    field: "staff_code",
    headerName: "員工編號",
    minWidth: 120,
    flex: 1,
  },
  {
    field: "full_name",
    headerName: "員工全名",
    minWidth: 120,
    flex: 1,
  },
  {
    field: "alias",
    headerName: "員工別名",
    minWidth: 120,
    flex: 1,
  },
  {
    field: "work_name",
    headerName: "上班地方",
    minWidth: 120,
    flex: 1,
  },
];
const checkInRecordSortComparator = (v1, v2) => {
  if (v1.text < v2.text) {
    return -1;
  }
  if (v1.text > v2.text) {
    return 1;
  }
  return 0;
};
const checkInRecordrenderCell = ({ value }) => {
  const { color, text } = value;
  return <Typography color={color}>{text}</Typography>;
};
export const checkInRecordTableColumn = [
  {
    field: "staff_code",
    headerName: "員工編號",
    minWidth: 120,
    flex: 1,
  },
  {
    field: "alias",
    headerName: "員工名稱",
    flex: 1,
  },
  {
    field: "date",
    headerName: "日期",
    flex: 1,
    valueFormatter: (value) => moment(value).format("YYYY-MM-DD"),
  },
  {
    field: "work_shortcode",
    headerName: "店舖編號",
    flex: 1,
    renderCell: (params) => {
      if (params.row.is_off || params.row.is_leave) {
        return "--";
      }
      return params.row.work_shortcode;
    },
  },
  {
    field: "work_name",
    headerName: "工作地點",
    minWidth: 120,
    flex: 1,
    renderCell: (params) => {
      if (params.row.is_off) {
        return params.row.off_type;
      } else if (params.row.is_leave) {
        return params.row.leave_type;
      }
      return params.row.work_name;
    },
  },
  {
    field: "time_range",
    headerName: "上班時段",
    minWidth: 120,
    flex: 1,
    renderCell: (params) => {
      const today = moment(params.row.date).format("YYYY-MM-DD");
      if (params.row.is_off || params.row.is_leave) {
        return "--";
      }
      if (!params.row.start_time && !params.row.end_time) {
        return "11:00 - 19:00";
      } else {
        return `${moment(`${today} ${params.row.start_time}`).format(
          "HH:mm"
        )} - ${moment(`${today} ${params.row.end_time}`).format("HH:mm")}`;
      }
    },
  },
  {
    field: "time_checkedin",
    headerName: "簽入時間",
    minWidth: 120,
    flex: 1,
    renderCell: checkInRecordrenderCell,
    valueGetter: (value, row) => {
      const today = moment(row.date).format("YYYY-MM-DD");
      const currentTime = moment();
      const time_checkin = moment(`${today} ${row.start_time ?? "11:00:00"}`);

      const time_checkedin = moment(`${row.time_checkedin}`);
      let text = "--";
      let color = "black";
      if (row.time_checkedin) {
        text = time_checkedin.format("HH:mm");
        if (time_checkedin.isSameOrBefore(time_checkin)) {
          color = "green";
        } else {
          color = "red";
        }
      } else if (row.is_off || row.is_leave) {
        text = "--";
      } else if (currentTime.isAfter(time_checkin)) {
        text = "未打卡";
        color = "red";
      }
      return { text, color };
    },
    sortComparator: checkInRecordSortComparator,
  },
  {
    field: "checkin_image",
    headerName: "簽入打卡方法",
    minWidth: 120,
    flex: 1,

    valueGetter: (value, row) => {
      if (row.checkin_image) {
        return "相片";
      } else if (
        !row.checkin_image &&
        row.checkin_latitude &&
        row.checkin_longitude
      ) {
        return "GPS";
      } else {
        return "--";
      }
    },
  },
  {
    field: "time_checkedout",
    headerName: "簽出時間",
    minWidth: 120,
    flex: 1,
    renderCell: checkInRecordrenderCell,
    valueGetter: (value, row) => {
      const today = moment(row.date).format("YYYY-MM-DD");
      const currentTime = moment();
      const time_checkout = moment(`${today} ${row.end_time ?? "19:00:00"}`);
      const time_checkedout = moment(`${row.time_checkedout}`);
      let text = "--";
      let color = "black";

      if (row.time_checkedout) {
        text = time_checkedout.format("HH:mm");
        if (time_checkedout.isSameOrAfter(time_checkout)) {
          color = "green";
        } else {
          color = "red";
        }
      } else if (row.is_off || row.is_leave) {
        text = "--";
      } else if (currentTime.isAfter(time_checkout)) {
        text = "未打卡";
        color = "red";
      }
      return { text, color };
    },
    sortComparator: checkInRecordSortComparator,
  },
  {
    field: "checkout_image",
    headerName: "簽出打卡方法",
    minWidth: 120,
    flex: 1,
    valueGetter: (value, row) => {
      if (row.checkout_image) {
        return "相片";
      } else if (
        !row.checkout_image &&
        row.checkout_latitude &&
        row.checkout_longitude
      ) {
        return "GPS";
      } else {
        return "--";
      }
    },
  },
];
export const StockOnHandByIdTableColumn = [
  {
    field: "m18_product_code",
    headerName: "m18號碼",
    minWidth: 120,
    flex: 2,
  },
  {
    field: "item_brand",
    headerName: "品牌",
    minWidth: 120,
    flex: 2,
  },

  {
    field: "item_name",
    headerName: "產品名稱",
    minWidth: 120,
    flex: 2,
  },
  {
    field: "stockonhand_qty",
    headerName: "倉存數量",
    minWidth: 120,
    flex: 2,
  },
  {
    field: "last_update_time",
    headerName: "最後更新時間",
    minWidth: 120,
    valueFormatter: (value) => moment(value).format("YYYY-MM-DD HH:mm:ss"),
    flex: 2,
  },
];
export function compareDataTableColumn(
  dateRanges,
  type,
  dateGrouping,
  isCompare = false,
  shopData = []
) {
  const uniqProduct = _.uniqBy(shopData, "product_master_id");
  const uniqCategory = _.uniqBy(shopData, "group_name");

  const columns = [];
  if (type === "product") {
    columns.push(
      ...[
        {
          field: "product_name",
          headerName: "Product Name",
          type: "singleSelect",
          valueOptions: ({ row }) => {
            const options = [];
            uniqProduct?.map((p) => options.push(p.product_name));
            return options.sort();
          },
          flex: 3,
        },
        {
          field: "m18_product_code",
          headerName: "M18 Code",
          type: "singleSelect",
          valueOptions: ({ row }) => {
            const options = [];
            uniqProduct?.map((p) => options.push(p.m18_product_code));
            return options.sort();
          },
          flex: 1,
        },
        {
          field: "mannings_product_code",
          headerName: "Mannings Code",
          type: "singleSelect",
          valueOptions: ({ row }) => {
            const options = [];
            uniqProduct?.map((p) => options.push(p.mannings_product_code));
            return options.sort();
          },
          flex: 1,
        },
      ]
    );
  } else if (type === "brand") {
    columns.push({
      field: "brand_name",
      headerName: "Brand Name",
      filterable: false,
      type: "singleSelect",
      flex: 1,
    });
  } else if (type === "shop") {
    columns.push(
      ...[
        {
          field: "product_name",
          headerName: "Product Name",
          type: "singleSelect",
          valueOptions: ({ row }) => {
            const options = [];
            uniqProduct?.map((p) => options.push(p.product_name));
            return options.sort();
          },
          flex: 2,
        },
        {
          field: "m18_product_code",
          headerName: "M18 Code",
          type: "singleSelect",
          valueOptions: ({ row }) => {
            const options = [];
            uniqProduct?.map((p) => options.push(p.m18_product_code));
            return options.sort();
          },
          flex: 1,
        },
        {
          field: "mannings_product_code",
          headerName: "Mannings Code",
          type: "singleSelect",
          valueOptions: ({ row }) => {
            const options = [];
            uniqProduct?.map((p) => options.push(p.mannings_product_code));
            return options.sort();
          },
          flex: 1,
        },
        {
          field: "group_name",
          headerName: "Category",
          type: "singleSelect",
          valueOptions: ({ row }) => {
            const options = [];
            uniqCategory?.map((p) => options.push(p.group_name));
            return options.sort();
          },
          flex: 1,
        },
      ]
    );
  }
  _.forEach(dateRanges, (dateRange, index) => {
    if (dateRange[0] && dateRange[1]) {
      const number = index + 1;
      if (dateGrouping === "selected_period") {
        columns.push(
          ...[
            {
              field: `qty${number}`,
              headerClassName: `comparison--${number}`,
              cellClassName: `comparison--${number}`,
              filterable: false,
              headerName: `Qty`,
              flex: 1,
              type: "number",
              valueFormatter: (value) =>
                `${new Intl.NumberFormat().format(value)}`,
            },
            {
              field: `amount${number}`,
              headerClassName: `comparison--${number}`,
              cellClassName: `comparison--${number}`,
              filterable: false,
              headerName: `Amount`,
              flex: 1,
              type: "number",
              valueFormatter: (value) =>
                `${new Intl.NumberFormat("en-US", {
                  style: "currency",
                  currency: "HKD",
                }).format(value)}`,
            },
          ]
        );
      } else {
        if (dateGrouping === "day" && type === "product") {
          columns.push({
            headerClassName: `comparison--${number}`,
            cellClassName: `comparison--${number}`,
            field: `product_discount_price${number}`,
            headerName: "Price",
            align: "right",
            headerAlign: "right",
            filterable: false,
            flex: 1,
            valueGetter: (value, row) => {
              if (
                value !== "0" &&
                value &&
                parseInt(row.product_unit_price) !== parseInt(value)
              ) {
                return row[`product_discount_price${number}`];
              } else {
                return row.product_unit_price ?? 0;
              }
            },
            renderCell: (params) => {
              return params.value !== "0" &&
                params.value &&
                parseInt(params.row.product_unit_price) !==
                  parseInt(params.value) ? (
                <div>
                  <b style={{ color: "red" }}>${params.value}</b>
                  {params.row[`discount_name${number}`]
                    ? `(${params.row[`discount_name${number}`]})`
                    : ""}
                </div>
              ) : (
                <div>
                  <b>${params.row.product_unit_price ?? 0}</b>
                </div>
              );
            },
          });
        }
        columns.push(
          ...[
            {
              field: `tx_date${number}`,
              headerClassName: `comparison--${number}`,
              cellClassName: `comparison--${number}`,
              headerName: "Date",
              align: "right",
              headerAlign: "right",
              flex: 1,
            },
            {
              field: `qty${number}`,
              headerClassName: `comparison--${number}`,
              cellClassName: `comparison--${number}`,
              filterable: false,
              headerName: "Qty",
              flex: 1.5,
              type: "number",
              valueFormatter: (value) =>
                `${new Intl.NumberFormat().format(value)}`,
            },
            {
              field: `amount${number}`,
              headerClassName: `comparison--${number}`,
              cellClassName: `comparison--${number}`,
              filterable: false,
              headerName: "Amount",
              flex: 1.5,
              type: "number",
              valueFormatter: (value) =>
                `${new Intl.NumberFormat("en-US", {
                  style: "currency",
                  currency: "HKD",
                }).format(value)}`,
            },
          ]
        );
      }
    }
  });

  if (isCompare) {
    columns.push(
      ...[
        {
          field: `variance_qty`,
          headerName: `Variance(Qty)`,
          flex: 1,
          type: "number",
          cellClassName: (params) => {
            if (params.value >= 0) {
              return "positive--cell";
            } else {
              return "negative--cell";
            }
          },
          valueFormatter: (value) =>
            `${new Intl.NumberFormat("en", { minimumFractionDigits: 1 }).format(
              _.round(value, 1)
            )}%`,
        },
        {
          field: `variance_amount`,
          headerName: "Variance(Amount)",
          flex: 1,
          type: "number",
          cellClassName: (params) => {
            if (params.value >= 0) {
              return "positive--cell";
            } else {
              return "negative--cell";
            }
          },
          valueFormatter: (value) =>
            `${new Intl.NumberFormat("en", { minimumFractionDigits: 1 }).format(
              _.round(value, 1)
            )}%`,
        },
      ]
    );
  }
  return columns;
}
export const StockOnHandTypeByIdTableColumn = [
  {
    field: "item_name",
    headerName: "貨品名稱",
    minWidth: 120,
    flex: 2,
    sortable: false,
  },
  {
    field: "opening_balance",
    headerName: "Opening Balance",
    minWidth: 30,
    flex: 2,
    sortable: false,
  },
  {
    field: "qty",
    headerName: "調整數量",
    minWidth: 30,
    flex: 2,
    sortable: false,
  },
  {
    field: "ending_balance",
    headerName: "Ending Balance",
    minWidth: 30,
    flex: 2,
    sortable: false,
  },
  {
    field: "types",
    headerName: "類別",
    minWidth: 80,
    flex: 2,
    sortable: false,
    valueFormatter: (value) => {
      let tempName = "N/A";
      switch (value) {
        case "adjust":
          tempName = "調整";
          break;
        case "giveaway":
          tempName = "贈品";
          break;
        case "refund":
          tempName = "退貨";
          break;
        case "refill":
          tempName = "補貨";
          break;
        case "system_init":
          tempName = "系統初始調整";
          break;
        case "exchangeFrom":
        case "exchangeTo":
          tempName = "換貨";
          break;
        case "return":
          tempName = "退倉";
          break;
        case "transferFrom":
        case "transferTo":
          tempName = "調貨";
          break;
        default:
          tempName = value;
          return tempName;
      }
      return tempName;
    },
  },
  {
    field: "reason",
    headerName: "原因",
    minWidth: 120,
    flex: 2,
    sortable: false,
    valueFormatter: (value) =>
      ["", null].some((item) => item === value) ? "N/A" : value,
  },
  {
    field: "remark",
    headerName: "備註",
    minWidth: 120,
    flex: 2,
    sortable: false,
    renderCell: (params) => {
      if (params.row.types === "giveaway" && !params.row.type_id)
        return "免登記";
      else return params.row.remark;
    },
  },
  {
    field: "created",
    headerName: "建立時間",
    minWidth: 180,
    flex: 2,
    valueFormatter: (value) => moment(value).format("YYYY-MM-DD HH:mm:ss"),
    sortable: false,
  },
];

export const PushNotificationTableColumn = [
  {
    field: "payload",
    headerName: "標題",
    minWidth: 120,
    flex: 2,
    sortable: false,
    valueFormatter: (value) => value.title,
  },
  {
    field: "status",
    headerName: "狀態",
    minWidth: 30,
    flex: 2,
    sortable: false,
  },
  {
    field: "response",
    headerName: "備註",
    minWidth: 30,
    flex: 2,
    sortable: false,
    renderCell: ({ value }) =>
      value ? (
        <div>
          <p style={{ margin: 0 }}>成功數量： {value.successCount}</p>
          <p style={{ margin: 0 }}>失敗數量： {value.failureCount}</p>
        </div>
      ) : (
        <></>
      ),
  },
  {
    field: "time_created",
    headerName: "建立時間",
    minWidth: 80,
    flex: 2,
    sortable: false,
    valueFormatter: (value) => moment(value).format("YYYY-MM-DD hh:mm:ss"),
  },
];

export function EmployeeBenefitTable1Column(types) {
  return [
    {
      field: "discount_month",
      headerName: "月份",
      flex: 1,
      editable: false,
    },
    {
      field: "brand_types_name",
      headerName: "品牌",
      flex: 1,
      type: "singleSelect",
      valueOptions: ({ row }) => {
        const options = [];
        types?.map((type) => options.push(type.brand_types_name));
        return options;
      },
      editable: false,
    },
    {
      field: "product_name",
      headerName: "產品",
      flex: 1,
      editable: false,
    },
    {
      field: "product_discount_price",
      headerName: "折扣價(BackOffice)",
      flex: 1,
      editable: false,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) =>
        `${new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "HKD",
        }).format(value)}`,
    },
    {
      field: "promoter_product_discount_price",
      headerName: "折扣價(Promoter)",
      flex: 1,
      editable: false,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) =>
        `${new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "HKD",
        }).format(value)}`,
    },
    {
      field: "remark",
      headerName: "備註",
      flex: 1,
      editable: false,
      valueFormatter: (value) => (value ? value : "--"),
    },
    {
      field: "item_quota",
      headerName: "限額",
      flex: 1,
      editable: false,
      align: "center",
      valueFormatter: (value) => (value ? value : "--"),
    },
    {
      field: "is_active",
      headerName: "狀態",
      flex: 1,
      editable: false,
      renderCell: ({ value }) => {
        return (
          <p style={{ color: value ? "blue" : "red" }}>
            {value ? "開啟" : "關閉"}
          </p>
        );
      },
    },
    {
      field: "created_by",
      headerName: "最後更新",
      flex: 1,
      editable: false,
    },
    {
      field: "time_created",
      headerName: "最後更新時間",
      flex: 1,
      editable: false,
      valueFormatter: (value) => moment(value).format("YYYY-MM-DD HH:mm:ss"),
    },
  ];
}
export const EmployeeBenefitTable2Column = [
  {
    field: "discount_month",
    headerName: "折扣月份",
    flex: 1,
    editable: false,
  },
  {
    field: "quota",
    headerName: "限額",
    flex: 1,
    editable: false,
    valueFormatter: (value) => `${value ? value : "--"}`,
  },
  {
    field: "msg",
    headerName: "備註",
    flex: 1,
    editable: false,
    valueFormatter: (value) => (value ? value : "--"),
  },
  {
    field: "cut_off_date",
    headerName: "截止日期",
    flex: 1,
    editable: false,
  },
  {
    field: "cut_off_time",
    headerName: "截止時間",
    flex: 1,
    editable: false,
    valueFormatter: (value) => moment(value, "HH:mm").format("hh:mm A"),
  },
  {
    field: "created_by",
    headerName: "最後更新",
    flex: 1,
    editable: false,
  },
  {
    field: "created_at",
    headerName: "最後更新時間",
    flex: 1,
    editable: false,
    valueFormatter: (value) => moment(value).format("YYYY-MM-DD HH:mm:ss"),
  },
];

export const EmployeeShoppingTableColumn = [
  {
    field: "id",
    headerName: "訂單號碼",
    flex: 1,
    editable: false,
  },
  {
    field: "tx_date",
    headerName: "購買日期",
    flex: 1,
    editable: false,
    valueFormatter: (value) => moment(value).format("YYYY-MM-DD"),
  },
  {
    field: "alias",
    headerName: "員工姓名",
    flex: 1,
    editable: false,
  },
  {
    field: "staff_code",
    headerName: "員工編號",
    flex: 1,
    editable: false,
  },
  {
    field: "total_discount_amount",
    headerName: "總數",
    flex: 1,
    editable: false,
    type: "number",
    valueFormatter: (value) =>
      `${new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "HKD",
      }).format(value)}`,
  },
  {
    field: "status",
    headerName: "狀態",
    flex: 1,
    editable: false,
    renderCell: ({ value }) => {
      switch (value) {
        case "已取貨":
          return <p style={{ color: "green" }}>{value}</p>;
        case "未取貨":
          return <p style={{ color: "red" }}>{value}</p>;
        default:
          return <p style={{ color: "blue" }}>{value}</p>;
      }
    },
  },
  {
    field: "attachment",
    headerName: "附件",
    editable: false,
    flex: 0.5,
    filterable: false,
    renderCell: EmployeeShoppingImagePreview,
  },
  {
    field: "signature",
    headerName: "簽署",
    editable: false,
    flex: 0.5,
    filterable: false,
    renderCell: EmployeeShoppingImagePreview,
  },
  {
    field: "time_signed",
    headerName: "取貨時間",
    flex: 1.5,
    editable: false,
    filterable: false,
    valueFormatter: (value) =>
      value ? moment(value).format("YYYY-MM-DD HH:mm:ss") : "--",
  },
  {
    field: "verified",
    headerName: "會計核對",
    flex: 1,
    editable: false,
    filterable: false,
    renderCell: VerifyButton,
  },
  {
    field: "approved",
    headerName: "HR核准",
    minWidth: 80,
    flex: 1,
    editable: false,
    filterable: false,
    renderCell: ApproveButton,
  },
  // {
  //   field: "remark",
  //   headerName: "備註",
  //   minWidth: 150,
  //   editable: false,
  //   renderCell: ({ value }) =>
  //     value ? <p style={{ color: "red" }}>{value}</p> : "",
  // },
  {
    field: "time_created",
    headerName: "建立時間",
    flex: 1.5,
    editable: false,
    filterable: false,
    valueFormatter: (value) => moment(value).format("YYYY-MM-DD HH:mm:ss"),
  },
];

export const questionnaireConfigTableColumn = [
  {
    field: "title",
    headerName: "標題",
    flex: 1,
    editable: false,
    valueFormatter: (value) => `${value ? value : "--"}`,
  },
  {
    field: "promoter",
    headerName: "員工數目",
    flex: 1,
    editable: false,
    valueFormatter: (value) => {
      return `${value ? value.length : "--"}`;
    },
  },
  {
    field: "answered_count",
    headerName: "已回答人數",
    flex: 1,
    editable: false,
  },
  {
    field: "start_date",
    headerName: "開始日期",
    flex: 1,
    editable: false,
    valueFormatter: (value) =>
      value ? moment(value).format("YYYY-MM-DD") : "--",
  },
  {
    field: "end_date",
    headerName: "結束日期",
    flex: 1,
    editable: false,
    valueFormatter: (value) =>
      value ? moment(value).format("YYYY-MM-DD") : "--",
  },
  {
    field: "created_by_user",
    headerName: "建立由",
    flex: 1,
    editable: false,
  },
  {
    field: "created_at",
    headerName: "建立日期",
    flex: 1,
    editable: false,
    valueFormatter: (value) => moment(value).format("YYYY/MM/DD HH:mm"),
  },
  {
    field: "modified_at",
    headerName: "修改日期",
    flex: 1,
    editable: false,
    valueFormatter: (value) => moment(value).format("YYYY/MM/DD HH:mm"),
  },
];

export const questionnaireReportListColumn = [
  {
    field: "title",
    headerName: "標題",
    flex: 1,
    editable: false,
    valueFormatter: (value) => `${value ? value : "--"}`,
  },
  {
    field: "promoter",
    headerName: "員工數目",
    flex: 1,
    editable: false,
    valueFormatter: (value) => {
      return `${value ? value.length : "--"}`;
    },
  },
  {
    field: "answered_count",
    headerName: "已回答人數",
    flex: 1,
    editable: false,
  },
  {
    field: "start_date",
    headerName: "開始日期",
    flex: 1,
    editable: false,
    valueFormatter: (value) => moment(value).format("YYYY/MM/DD"),
  },
  {
    field: "end_date",
    headerName: "結束日期",
    flex: 1,
    editable: false,
    valueFormatter: (value) => moment(value).format("YYYY/MM/DD"),
  },
];

export const giveawayInvoiceColumn = [
  {
    field: "staff_code",
    headerName: "員工編號",
    flex: 1,
    editable: false,
  },
  {
    field: "alias",
    headerName: "員工名稱",
    flex: 1,
    editable: false,
  },
  {
    field: "fullname",
    headerName: "客人名稱",
    flex: 1,
    editable: false,
    valueGetter: (value, row) => {
      if (row.register_types_id === 1) {
        return value;
      } else return "免登記";
    },
  },
  {
    field: "work_name",
    headerName: "換領地點",
    flex: 1,
    editable: false,
  },
  {
    field: "sum_of_purchase",
    headerName: "購買數量",
    align: "right",
    headerAlign: "right",
    flex: 1,
    valueGetter: (value, row) => {
      return value ? parseInt(value) : 0;
    },
    editable: false,
  },
  {
    field: "sum_of_giveaway",
    headerName: "換領數量",
    align: "right",
    headerAlign: "right",
    flex: 1,
    editable: false,
    valueGetter: (value, row) => {
      return value ? parseInt(value) : 0;
    },
  },
  {
    field: "all_sum_of_giveaway",
    headerName: "換領總數量",
    align: "right",
    headerAlign: "right",
    flex: 1,
    editable: false,
    valueGetter: (value, row) => {
      return value ? parseInt(value) : 0;
    },
  },
  {
    field: "invoice_date",
    headerName: "換領日期",
    flex: 1,
    editable: false,
    valueFormatter: (value) => moment(value).format("YYYY/MM/DD"),
  },
  {
    field: "is_checked",
    headerName: "檢查狀態",
    align: "center",
    headerAlign: "center",
    editable: false,
    type: "singleSelect",
    valueOptions: ({ row }) => {
      const options = ["已檢查", "待檢查"];
      return options;
    },
    valueGetter: (value, row) => {
      return value ? "已檢查" : "待檢查";
    },
    renderCell: (params) => {
      return (
        <Chip
          variant={params.row.is_checked ? "filled" : "outlined"}
          label={params.row.is_checked ? "已檢查" : "待檢查"}
          color={params.row.is_checked ? "success" : "info"}
        />
      );
    },
  },
];
